import React, { FC } from "react";
import clsx from "clsx";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { ClickAwayListener } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import OverflowTooltip from "../OverflowTooltip/OverflowTooltip";
import Masker from "../Masker/Masker";
import { MaskedLevel } from "../../types";

const useStyles = makeStyles(() => ({
  main: {
    position: "relative",
  },
  container: {
    maxWidth: 237,
    maxHeight: 232,
    padding: "10px 10px 0px 10px",
  },
  useGrid: {
    display: "grid",
    gridTemplateColumns: "32px 173px",
    gridColumnGap: 12,
  },
  expandedContainer: {
    position: "absolute",
    maxHeight: 232,
    background: "#FFF",
    borderRadius: 8,
    outline: "1px solid #DBDBDB",
    paddingBottom: 10,
    zIndex: 2,
  },
  iconContainer: {
    width: 42,
    height: 42,
    background: "#EEEFF3",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  locationIcon: {
    fontSize: 20,
  },
  branchColor: {
    color: "#5351FB",
  },
  content: {
    maxWidth: 214,
  },
  subContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    maxHeight: 185,
  },
  titleArrowContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  title: {
    fontSize: 12,
    color: "#231F20",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  icon: {
    width: 32,
    height: 32,
  },
  arrowIcon: {
    color: "#858997",
    "&:hover": {
      cursor: "pointer",
    },
    "&.MuiSvgIcon-root": {
      width: 12,
      height: 12,
    },
  },
  arrowIconExpanded: {
    transform: "rotate(90deg)",
  },
  previewContainer: {
    height: 20,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  expandedPreviewContainer: {
    paddingRight: 14,
    fontSize: 12,
    color: "#858997",
  },
  expandedContentContainer: {
    fontSize: 14,
    color: "#858997",
    overflowY: "scroll",
  },
}));

interface ICardFieldProps {
  id?: number;
  Icon?: FC;
  title: string;
  isExpanded?: boolean;
  isMasked?: boolean;
  PreviewContent?: FC | string[];
  onClickAway?: () => void;
  ExpandedContent?: FC;
  onExpand?: (any) => any;
}

const CardFieldContent = ({
  id,
  Icon,
  title,
  onExpand,
  isExpanded,
  isMasked = false,
  PreviewContent,
  ExpandedContent,
}) => {
  const classes = useStyles();

  return (
    <div
      className={clsx(
        classes.container,
        isExpanded && classes.expandedContainer,
        Icon && classes.useGrid,
      )}
    >
      {Icon && <Icon className={classes.icon} />}
      <div className={classes.content}>
        <div className={classes.titleArrowContainer}>
          <span className={classes.title}>{title}</span>
          {ExpandedContent && (
            <ArrowForwardIosIcon
              onClick={onExpand && (() => onExpand(id))}
              className={clsx(
                classes.arrowIcon,
                isExpanded && classes.arrowIconExpanded,
              )}
            />
          )}
        </div>
        <Masker
          className={classes.subContent}
          isMasked={isMasked}
          maskedLevel={MaskedLevel.Low}
        >
          {isExpanded ? (
            ExpandedContent && (
              <div
                className={clsx(
                  classes.expandedContentContainer,
                  "transparent-scrollbar",
                )}
              >
                <ExpandedContent />
              </div>
            )
          ) : (
            <div
              className={clsx(
                classes.previewContainer,
                classes.expandedPreviewContainer,
              )}
            >
              {typeof PreviewContent === "function" ? (
                <PreviewContent />
              ) : (
                <OverflowTooltip text={PreviewContent?.join(", ")} />
              )}
            </div>
          )}
        </Masker>
      </div>
    </div>
  );
};

export const CardField: FC<ICardFieldProps> = React.memo(
  ({
    id,
    Icon,
    title,
    onExpand,
    onClickAway = () => null,
    isExpanded = false,
    isMasked = false,
    PreviewContent,
    ExpandedContent,
  }) => {
    const classes = useStyles();

    return (
      <div className={classes.main}>
        {isExpanded ? (
          <ClickAwayListener onClickAway={onClickAway}>
            <div>
              <CardFieldContent
                id={id}
                Icon={Icon}
                title={title}
                onExpand={onExpand}
                isExpanded={isExpanded}
                isMasked={isMasked}
                PreviewContent={PreviewContent}
                ExpandedContent={ExpandedContent}
              />
            </div>
          </ClickAwayListener>
        ) : (
          <CardFieldContent
            id={id}
            Icon={Icon}
            title={title}
            onExpand={onExpand}
            isExpanded={isExpanded}
            isMasked={isMasked}
            PreviewContent={PreviewContent}
            ExpandedContent={ExpandedContent}
          />
        )}
      </div>
    );
  },
);
